import React, { useCallback, useMemo, useState } from "react";

import { faSortAmountDown } from "@fortawesome/pro-solid-svg-icons/faSortAmountDown";
import { faSortAmountUp } from "@fortawesome/pro-solid-svg-icons/faSortAmountUp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "antd/es/col";
import Dropdown from "antd/es/dropdown";
import Row from "antd/es/row";
import Select from "antd/es/select";
import { useIntl } from "react-intl";

import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { Button } from "@mapmycustomers/ui";

import SelectField from "@app/component/input/SelectField";
import useView from "@app/component/view/utils/useView";
import layout from "@app/styles/layout";
import useAnalytics from "@app/util/contexts/useAnalytics";
import dealFieldModel from "@app/util/fieldModel/DealFieldModel";

import styles from "./CardFilter.module.scss";
import getSortDirectionNameForField from "./util/getSortDirectionNameForField";

interface Props {}

const CardSorter: React.FC<Props> = () => {
  const intl = useIntl();
  const analyticIssuer = useAnalytics();

  const { updateViewState, viewState } = useView();

  const [field, setField] = useState<IField>(viewState.sort[0].field);
  const [order, setOrder] = useState<SortOrder>(viewState.sort[0].order);
  const [visible, setVisibility] = useState(false);

  const handleApply = useCallback(() => {
    updateViewState({ sort: [{ field, order }] });
    setVisibility(false);
    analyticIssuer?.completed(["Sort Criteria Button", "Apply"]);
  }, [analyticIssuer, order, field, updateViewState]);

  const handleSelectField = useCallback((name: string) => {
    const field = dealFieldModel.getByName(name);
    if (field) {
      setField(field);
    }
  }, []);

  const fieldOptions = useMemo(
    () =>
      ["closingDate", "createdAt", "updatedAt", "amount", "daysInStage"].map((fieldName) => ({
        label: dealFieldModel.getByName(fieldName)?.displayName,
        value: fieldName,
      })),
    []
  );

  const handleSortingAnalytics = useCallback(() => {
    analyticIssuer?.initiated(["Sort Criteria Button"]);
  }, [analyticIssuer]);

  const handleSortingCancel = useCallback(() => {
    setVisibility(false);
    analyticIssuer?.cancelled(["Sort Criteria Button", "Cancel"]);
  }, [analyticIssuer, setVisibility]);

  return (
    <Dropdown
      onVisibleChange={setVisibility}
      overlay={
        <div className={styles.container}>
          <Row gutter={[layout.spacerS, layout.spacerM]}>
            <Col span={24}>
              <SelectField<string>
                className={styles.select}
                label={intl.formatMessage({
                  id: "deals.cardViews.sorter.field",
                  defaultMessage: "Sort criteria",
                  description: "Deal card card filter - Sort criteria",
                })}
                onChange={handleSelectField}
                options={fieldOptions}
                value={field.name}
              />
            </Col>
            <Col span={24}>
              <SelectField<SortOrder>
                className={styles.select}
                label={intl.formatMessage({
                  id: "dealCards.cardFilter.sortDirection",
                  defaultMessage: "Sort direction",
                  description: "Deal card card filter - Sort direction",
                })}
                onChange={setOrder}
                value={order}
              >
                <Select.Option value={SortOrder.DESC}>
                  {getSortDirectionNameForField(intl, field.name, SortOrder.DESC)}
                </Select.Option>
                <Select.Option value={SortOrder.ASC}>
                  {getSortDirectionNameForField(intl, field.name, SortOrder.ASC)}
                </Select.Option>
              </SelectField>
            </Col>
            <Col span={24}>
              <Row gutter={layout.spacerS} justify="end">
                <Col>
                  <Button onClick={handleSortingCancel}>
                    {intl.formatMessage({
                      id: "dealCards.cardFilter.cancel",
                      defaultMessage: "Cancel",
                      description: "Deal card card filter - cancel",
                    })}
                  </Button>
                </Col>
                <Col>
                  <Button onClick={handleApply} type="primary">
                    {intl.formatMessage({
                      id: "dealCards.cardFilter.apply",
                      defaultMessage: "Apply",
                      description: "Deal card card filter - apply",
                    })}
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      }
      trigger={["click"]}
      visible={visible}
    >
      <Button
        leftIcon={
          <FontAwesomeIcon
            icon={viewState.sort[0].order === SortOrder.DESC ? faSortAmountDown : faSortAmountUp}
          />
        }
        onClick={handleSortingAnalytics}
      >
        {viewState.sort[0].field.displayName}
      </Button>
    </Dropdown>
  );
};

export default CardSorter;
